/**
 * Adapters mapping the REST check-in responses to the shapes the check-in app's
 * teacher hooks/components already consume (previously served by GraphQL).
 *
 * The backend now attaches name-resolved `*By` relations
 * (`checkedInParentBy`, `checkedOutParentBy`, …) directly, so those pass
 * through unchanged; the work here is reshaping aggregates and pagination.
 */

export interface AgeGroupCount {
    totalCheckedIn: number
    totalCheckedOut: number
    totalStillCheckedIn: number
}

export interface AggregateByAgeGroup {
    Toddlers: AgeGroupCount
    PreSchool: AgeGroupCount
    SchoolAge: AgeGroupCount
    Samuels: AgeGroupCount
    Teenagers: AgeGroupCount
}

const emptyCount = (): AgeGroupCount => ({
    totalCheckedIn: 0,
    totalCheckedOut: 0,
    totalStillCheckedIn: 0
})

export const emptyAggregate = (): AggregateByAgeGroup => ({
    Toddlers: emptyCount(),
    PreSchool: emptyCount(),
    SchoolAge: emptyCount(),
    Samuels: emptyCount(),
    Teenagers: emptyCount()
})

/** Map a backend ageGroup label (canonical or legacy) to a UI bucket key. */
function normalizeAgeGroupKey(label: string): keyof AggregateByAgeGroup | null {
    const l = label.toLowerCase().replace(/[\s_-]/g, '')
    if (l.startsWith('toddler')) return 'Toddlers'
    if (l.startsWith('preschool')) return 'PreSchool'
    if (l.startsWith('schoolage')) return 'SchoolAge'
    if (l.startsWith('samuel')) return 'Samuels'
    if (l.startsWith('teen')) return 'Teenagers'
    return null
}

interface RestAggregateRow {
    ageGroup: string
    checkedIn: number
    checkedOut: number
    stillCheckedIn?: number
}

/**
 * Convert the REST `[{ageGroup, checkedIn, checkedOut}]` array into the
 * keyed object (`{Toddlers:{totalCheckedIn,totalCheckedOut}, …}`) the
 * dashboard's AggregateCards render. Unrecognized legacy labels are ignored.
 */
export function adaptAggregates(raw: unknown): AggregateByAgeGroup {
    const result = emptyAggregate()
    if (!Array.isArray(raw)) return result
    for (const row of raw as RestAggregateRow[]) {
        const key = normalizeAgeGroupKey(String(row?.ageGroup ?? ''))
        if (!key) continue
        result[key].totalCheckedIn += Number(row.checkedIn ?? 0)
        result[key].totalCheckedOut += Number(row.checkedOut ?? 0)
        result[key].totalStillCheckedIn += Number(row.stillCheckedIn ?? 0)
    }
    return result
}

export interface FindAllCheckinsResult {
    checkInResults: Record<string, unknown>[]
    checkInAggregates: {
        totalCheckedIn: number
        totalCheckedOut: number
        totalStillCheckedIn: number
    }
    pagination?: {
        currentPage: number
        totalCount: number
        totalPages: number
        isFirstPage: boolean
        isLastPage: boolean
        hasNextPage: boolean
        hasPreviousPage: boolean
    }
}

/** Reshape the REST list response to the GraphQL-era `findAllCheckins` shape. */
export function adaptFindAllCheckins(raw: unknown): FindAllCheckinsResult {
    const data = (raw ?? {}) as {
        checkInResults?: Record<string, unknown>[]
        checkInAggregates?: {
            totalCheckedIn?: number
            totalCheckedOut?: number
            totalStillCheckedIn?: number
        }
        pagination?: {
            page?: number
            limit?: number
            total?: number
            totalPages?: number
        }
    }
    const p = data.pagination
    return {
        checkInResults: data.checkInResults ?? [],
        checkInAggregates: {
            totalCheckedIn: data.checkInAggregates?.totalCheckedIn ?? 0,
            totalCheckedOut: data.checkInAggregates?.totalCheckedOut ?? 0,
            totalStillCheckedIn: data.checkInAggregates?.totalStillCheckedIn ?? 0
        },
        pagination: p
            ? {
                  currentPage: p.page ?? 1,
                  totalCount: p.total ?? 0,
                  totalPages: p.totalPages ?? 0,
                  isFirstPage: (p.page ?? 1) <= 1,
                  isLastPage: (p.page ?? 1) >= (p.totalPages ?? 0),
                  hasNextPage: (p.page ?? 1) < (p.totalPages ?? 0),
                  hasPreviousPage: (p.page ?? 1) > 1
              }
            : undefined
    }
}

/** Checkout-queue and stale items already match the UI shape (enriched `*By`). */
export function adaptCheckinList(raw: unknown): Record<string, unknown>[] {
    return Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
}
